import {useEffect, useState} from 'react';
import cx from 'classnames';

import {LabResultMetricTypeEnum} from '#apollo/schema';
import {AnimatedText, Icon, NumberTicker, Text} from '#component/base';
import {Transition} from '#component/util';
import {sprinkles} from '#style/sprinkles';
import {theme} from '#theme';

import {StepProps} from '../../StorySteps.types';
import {
  animTextMiddleCenteredNarrow,
  animTextTopCentered,
  animTextTopCenteredNarrow,
  menuTextTopCentered,
} from '../StorySteps.css';
import {InformationModal} from './InformationModal/InformationModal';

import * as css from './Step4_1.css';

export const Step4_1 = ({percentiles, show}: StepProps) => {
  const [animateGraph, setAnimateGraph] = useState(false);

  const [openInformationModal, setOpenInformationModal] = useState(false);

  const menuItems = [
    {type: LabResultMetricTypeEnum.TallyGeneralAgePercentile, label: 'Everyone'},
    {type: LabResultMetricTypeEnum.TallyCohortAgePercentile, label: 'Your Peers'},
  ];
  const [activeTab, setActiveTab] = useState(menuItems[0]);

  const handleMenuItemClick = (item: any) => {
    setActiveTab(item);
  };

  useEffect(() => {
    if (show) {
      const graphTimeout = setTimeout(() => {
        setAnimateGraph(true);
      }, 500);
      return () => {
        clearTimeout(graphTimeout);
      };
    } else {
      setAnimateGraph(false);
    }
  }, [show]);

  useEffect(() => {
    return () => {
      setAnimateGraph(false);
    };
  }, []);

  const handleOpenInformationModal = () => {
    setOpenInformationModal(true);
  };

  const cohortAgePercentile = percentiles?.find(
    item => item.type === LabResultMetricTypeEnum.TallyCohortAgePercentile,
  );
  const minCohortAgePercentile =
    cohortAgePercentile?.percentile !== undefined &&
    cohortAgePercentile?.percentile > 0 &&
    cohortAgePercentile?.percentile < 25
      ? 25.0
      : cohortAgePercentile?.percentile || 0;
  const hasPeerData = cohortAgePercentile?.percentile == 0 ? false : true;
  const generalAgePercentile = percentiles?.find(
    item => item.type === LabResultMetricTypeEnum.TallyGeneralAgePercentile,
  );
  const minGeneralAgePercentile =
    generalAgePercentile?.percentile !== undefined &&
    generalAgePercentile?.percentile > 0 &&
    generalAgePercentile?.percentile < 25
      ? 25.0
      : generalAgePercentile?.percentile || 0;

  const openingLabel =
    activeTab.type == LabResultMetricTypeEnum.TallyCohortAgePercentile && !hasPeerData
      ? 'Curious how you stack up to your peers?'
      : 'Your TallyAge™ is better than';
  const displayedPercentile =
    activeTab.type == LabResultMetricTypeEnum.TallyCohortAgePercentile
      ? minCohortAgePercentile
      : minGeneralAgePercentile;

  const roundedPercentileValue = displayedPercentile ? Number(displayedPercentile.toFixed(1)) : 0;
  const roundedPercentileDisplay = displayedPercentile ? displayedPercentile.toFixed(1) : '0.0';

  const percentileDigitsLength = `${roundedPercentileDisplay}`.split('').length;

  const percentileLabel =
    activeTab.type == LabResultMetricTypeEnum.TallyCohortAgePercentile
      ? 'of your peers'
      : 'of the Tally Health community';

  const informationModalLabel =
    activeTab.type == LabResultMetricTypeEnum.TallyCohortAgePercentile
      ? 'Who are my peers?'
      : 'Who is everyone?';

  const getScaledValue = (value: number) => {
    // scales value below 50% to ensure 25% aligns with 1/4 of the graph
    if (value < 30) {
      return value * 1.2;
    }
    // keep 50% exactly at the halfway point
    if (value === 50) {
      return 50;
    }
    // scales value above 50% to ensure 75% aligns with 3/4 of the graph
    return 50 + (value - 50) * 0.8;
  };

  return (
    <Transition className={sprinkles({position: 'absolute', inset: 0})} show={show}>
      <div className={css.rightContentContainer}>
        <div
          className={menuTextTopCentered}
          style={{opacity: show ? 1 : 0, transition: show ? 'opacity 0.5s' : 'opacity 0s'}}>
          <ul className={css.menuHeader}>
            {menuItems.map((item, index) => (
              <li
                className={cx(
                  css.menuItem,
                  item.label.toLowerCase().includes(activeTab.label.toLowerCase()) &&
                    css.activeLinkMenu,
                )}
                key={index}
                style={{margin: '0 1px 0px 15px', pointerEvents: 'auto'}}>
                <Text
                  variant="bodyLarge"
                  className={cx(
                    css.menuItemText,
                    item.label.toLowerCase().includes(activeTab.label.toLowerCase()) &&
                      css.activeMenuItemText,
                  )}
                  onClick={() => handleMenuItemClick(item)}>
                  {item.label}
                </Text>
              </li>
            ))}
          </ul>
        </div>
        <div>
          {activeTab.type == LabResultMetricTypeEnum.TallyGeneralAgePercentile ||
          (activeTab.type === LabResultMetricTypeEnum.TallyCohortAgePercentile && hasPeerData) ? (
            <div>
              <div className={animTextTopCentered}>
                <AnimatedText
                  className={css.openingLabel}
                  as="span"
                  color="white"
                  variant="bodyXLarge"
                  fontWeight="regular">
                  {openingLabel}
                </AnimatedText>
              </div>

              <div>
                <Text
                  as="div"
                  color="white"
                  className={css.percentile}
                  style={{
                    opacity: animateGraph ? 1 : 0,
                    transition: animateGraph
                      ? `opacity 0.5s ${theme.transitions.storyEasing}`
                      : 'opacity 0s',
                  }}>
                  <div className={sprinkles({content: 'center', flex: 'flex', items: 'start'})}>
                    {animateGraph ? (
                      <NumberTicker className={css.percentileNumber} color="white">
                        {roundedPercentileDisplay}
                      </NumberTicker>
                    ) : (
                      <Text className={css.percentileNumber} color="white" style={{opacity: 0}}>
                        {'0'.repeat(percentileDigitsLength)}
                      </Text>
                    )}
                    <span className={css.percentageSign}>%</span>
                  </div>

                  <Text className={css.percentileLabel} color="white" variant="bodyLarge">
                    {percentileLabel}
                  </Text>
                </Text>
              </div>
            </div>
          ) : (
            <div>
              <AnimatedText className={animTextTopCenteredNarrow} as="span" color="white">
                Curious how you stack up to your peers?
              </AnimatedText>
              <AnimatedText
                className={animTextMiddleCenteredNarrow}
                as="span"
                color="white"
                variant="bodyLarge">
                Make sure your birth sex is set in your user profile.
              </AnimatedText>
            </div>
          )}
        </div>
      </div>
      <Transition.Child className={css.graph}>
        <div
          className={css.graphHighlight}
          style={{
            clipPath: `inset(0 ${
              animateGraph ? `${100 - getScaledValue(roundedPercentileValue || 0)}%` : '100%'
            } 0 0)`,
          }}
        />
        <div
          className={css.graphFilledDotsHighlight}
          style={{
            clipPath: `inset(0 ${
              animateGraph ? `${100 - getScaledValue(roundedPercentileValue || 0)}%` : '100%'
            } 0 0)`,
          }}
        />
        {(activeTab.type === LabResultMetricTypeEnum.TallyGeneralAgePercentile ||
          !(
            activeTab.type === LabResultMetricTypeEnum.TallyCohortAgePercentile && !hasPeerData
          )) && (
          <div
            className={css.graphTicker}
            style={{
              transform: `translateX(${
                animateGraph
                  ? `${Math.min(getScaledValue(roundedPercentileValue || 0), 91)}%`
                  : '0%'
              })`,
            }}
          />
        )}
      </Transition.Child>
      <Transition.Child className={css.informationModal}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <Text
            className={css.informationLabel}
            as="h4"
            variant="caption"
            color="pink"
            onClick={handleOpenInformationModal}
            textAlign="center">
            {informationModalLabel}
          </Text>
          <Icon name="Info" color="pink" style={{marginLeft: '8px'}} />
        </div>
        {openInformationModal && (
          <InformationModal
            open={openInformationModal}
            onOpenChange={setOpenInformationModal}
            percentileType={activeTab.type}
          />
        )}
      </Transition.Child>
    </Transition>
  );
};
